import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  Divider,
  Stack,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';
import { CONFIG } from '../../config/config';
import {
  getEntityHighLevelInfo,
  EntityHighLevelInfoResponse,
} from '../../services/api';
import { formatDisplayDate } from '../../utils/dateUtils';

interface DemographicsTabProps {
  parentEntityId: number;
  childCategory: string;
  isLeafEntity?: boolean;
  onEntitySelect?: (entityId: number) => void;
  onNavigateToTab?: (tabIndex: number) => void;
}

interface DistributionItem {
  name?: string;
  display_label?: string;
  status?: string;
  attribute_value?: string;
  bucket?: string;
  count: number;
}

interface EntityDateItem {
  id: number;
  name: string;
  valid_from?: string;
  valid_till?: string;
  entity_type: string;
}

interface AttributeKey {
  id: number;
  name: string;
  display_label: string;
}

const DemographicsTab: React.FC<DemographicsTabProps> = ({
  parentEntityId,
  childCategory,
  isLeafEntity = false,
  onEntitySelect,
  onNavigateToTab,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [entityTypeDistribution, setEntityTypeDistribution] = useState<DistributionItem[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<DistributionItem[]>([]);
  const [entitiesStarting, setEntitiesStarting] = useState<EntityDateItem[]>([]);
  const [entitiesClosing, setEntitiesClosing] = useState<EntityDateItem[]>([]);
  const [attributeKeys, setAttributeKeys] = useState<AttributeKey[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const [attributeDistribution, setAttributeDistribution] = useState<DistributionItem[]>([]);
  const [isNumericAttribute, setIsNumericAttribute] = useState(false);
  
  // Entity high-level info
  const [entityInfo, setEntityInfo] = useState<EntityHighLevelInfoResponse | null>(null);
  const [entityInfoLoading, setEntityInfoLoading] = useState(false);
  const theme = useTheme();

  // Fetch entity high-level info
  useEffect(() => {
    if (!parentEntityId) return;
    
    const fetchEntityInfo = async () => {
      setEntityInfoLoading(true);
      try {
        const response = await getEntityHighLevelInfo(parentEntityId);
        setEntityInfo(response.data);
      } catch (err) {
        console.error('Error fetching entity info:', err);
      } finally {
        setEntityInfoLoading(false);
      }
    };
    fetchEntityInfo();
  }, [parentEntityId]);

  // Fetch demographics summary
  useEffect(() => {
    // For leaf entities, show entity details instead of children demographics
    if (isLeafEntity) {
      const fetchEntityDetails = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(
            `${CONFIG.SERVER_URL}/dashboard/demographics/entity?entity_id=${parentEntityId}`
          );
          if (!response.ok) throw new Error('Failed to fetch entity details');
          const data = await response.json();
          
          // For leaf entity, we show the entity's own info
          setEntityTypeDistribution([]);
          setStatusDistribution([]);
          setEntitiesStarting([]);
          setEntitiesClosing([]);
          setAttributeKeys(data.attribute_keys || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        } finally {
          setLoading(false);
        }
      };
      fetchEntityDetails();
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/demographics/summary?parent_entity_id=${parentEntityId}&child_category=${encodeURIComponent(childCategory)}`
        );
        if (!response.ok) throw new Error('Failed to fetch demographics data');
        const data = await response.json();
        
        setEntityTypeDistribution(data.entity_type_distribution?.distribution || []);
        setStatusDistribution(data.status_distribution?.distribution || []);
        setEntitiesStarting(data.entities_starting_this_month || []);
        setEntitiesClosing(data.entities_closing_this_month || []);
        setAttributeKeys(data.available_attribute_keys || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    
    if (parentEntityId && childCategory) {
      fetchData();
    }
  }, [parentEntityId, childCategory]);

  // Fetch attribute distribution when attribute is selected
  useEffect(() => {
    if (!selectedAttribute) {
      setAttributeDistribution([]);
      return;
    }
    
    const fetchAttributeDistribution = async () => {
      try {
        const response = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/demographics/attribute-distribution?parent_entity_id=${parentEntityId}&child_category=${encodeURIComponent(childCategory)}&attribute_key=${encodeURIComponent(selectedAttribute)}&is_numeric=${isNumericAttribute}`
        );
        if (!response.ok) throw new Error('Failed to fetch attribute distribution');
        const data = await response.json();
        setAttributeDistribution(data.distribution || []);
      } catch (err) {
        console.error('Error fetching attribute distribution:', err);
      }
    };
    
    fetchAttributeDistribution();
  }, [parentEntityId, childCategory, selectedAttribute, isNumericAttribute]);

  const getPieChartOption = (data: DistributionItem[], title: string, labelKey: string) => {
    const chartData = data.map(item => ({
      name: (item as any)[labelKey] || 'Unknown',
      value: item.count,
    }));

    return {
      title: {
        text: title,
        left: 'center',
        textStyle: { fontSize: 14, color: theme.palette.text.primary },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        show: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
        type: chartData.length > 8 ? 'scroll' : 'plain',
        data: chartData.map((d) => d.name),
        itemGap: 10,
        textStyle: {
          fontSize: 12,
          color: theme.palette.text.primary,
        },
        formatter: (name: string) => (name === '' ? '—' : name),
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 12,
              fontWeight: 'bold',
            },
          },
          data: chartData,
        },
      ],
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Entity High-Level Info Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Entity Overview
            </Typography>
            
            {entityInfoLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : entityInfo ? (
              <Grid container spacing={2}>
                {/* Entity Name and Type */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" fontWeight="bold">
                      {entityInfo.entity.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {entityInfo.entity.entity_type_display_label} • {entityInfo.entity.status}
                    </Typography>
                    {entityInfo.entity.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {entityInfo.entity.description}
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* === DYNAMIC INFORMATION (changes with dates) - SHOWN FIRST === */}
                
                {/* Open RAIDs */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon color="error" fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Open RAIDs
                          </Typography>
                        </Stack>
                        <Tooltip title="Go to RAIDs tab">
                          <IconButton size="small" onClick={() => onNavigateToTab?.(5)} color="primary">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography variant="h4" color={entityInfo.open_raid_count > 0 ? 'error.main' : 'success.main'}>
                        {entityInfo.open_raid_count}
                      </Typography>
                      {entityInfo.open_raid_by_category.length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                          {entityInfo.open_raid_by_category.map((cat) => (
                            <Chip
                              key={cat.raid_category}
                              label={`${cat.raid_category}: ${cat.count}`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Last Review */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Last Review
                          </Typography>
                        </Stack>
                        <Tooltip title="Go to Review Status tab">
                          <IconButton size="small" onClick={() => onNavigateToTab?.(4)} color="primary">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {entityInfo.last_review ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {entityInfo.last_review.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {entityInfo.last_review.date ? formatDisplayDate(entityInfo.last_review.date) : 'N/A'} • {entityInfo.last_review.status}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No reviews completed</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Last Metric Date */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AssessmentIcon color="secondary" fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Last Metric Computation
                          </Typography>
                        </Stack>
                        <Tooltip title="Go to Metrics tab">
                          <IconButton size="small" onClick={() => onNavigateToTab?.(3)} color="primary">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography variant="body2" fontWeight="medium">
                        {entityInfo.last_metric_date ? formatDisplayDate(entityInfo.last_metric_date) : 'No metrics computed'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Child Entities Count - Dynamic as children can change */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <GroupIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Child Entities
                          </Typography>
                        </Stack>
                        {entityInfo.child_count > 0 && (
                          <Tooltip title="Go to Hierarchy tab">
                            <IconButton size="small" onClick={() => onNavigateToTab?.(1)} color="primary">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="h4" color="primary.main">
                        {entityInfo.child_count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Unable to load entity information
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Entity Type Distribution - only show if data available */}
        {entityTypeDistribution.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 350 }}>
              <ReactECharts
                option={getPieChartOption(entityTypeDistribution, 'Entity Type Distribution', 'display_label')}
                style={{ height: '100%' }}
                opts={{ renderer: 'canvas' }}
              />
            </Paper>
          </Grid>
        )}

        {/* Status Distribution - only show if data available */}
        {statusDistribution.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 350 }}>
              <ReactECharts
                option={getPieChartOption(statusDistribution, 'Status Distribution', 'status')}
                style={{ height: '100%' }}
                opts={{ renderer: 'canvas' }}
              />
            </Paper>
          </Grid>
        )}

        {/* Entities Starting This Month */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Entities Starting This Month
              <Chip label={entitiesStarting.length} size="small" sx={{ ml: 1 }} />
            </Typography>
            {entitiesStarting.length > 0 ? (
              <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
                {entitiesStarting.map((entity) => (
                  <ListItem key={entity.id} divider>
                    <ListItemText
                      primary={entity.name}
                      secondary={`${entity.entity_type} • Starts: ${entity.valid_from || 'N/A'}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No entities starting this month
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Entities Closing This Month */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Entities Closing This Month
              <Chip label={entitiesClosing.length} size="small" color="warning" sx={{ ml: 1 }} />
            </Typography>
            {entitiesClosing.length > 0 ? (
              <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
                {entitiesClosing.map((entity) => (
                  <ListItem key={entity.id} divider>
                    <ListItemText
                      primary={entity.name}
                      secondary={`${entity.entity_type} • Ends: ${entity.valid_till || 'N/A'}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No entities closing this month
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* === TEAM & RELATIONSHIPS (static info) - AT THE VERY BOTTOM === */}
        {entityInfo && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon color="primary" />
                Team & Relationships
              </Typography>
              <Grid container spacing={2}>
                {/* Owner */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <PersonIcon color="success" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Owner
                        </Typography>
                      </Stack>
                      {entityInfo.owner ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'success.main' }}>
                            {entityInfo.owner.display_name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium">
                            {entityInfo.owner.display_name}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not assigned</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Reviewers */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <GroupIcon color="info" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Reviewers
                        </Typography>
                        <Chip label={entityInfo.reviewers.length} size="small" color="info" />
                      </Stack>
                      {entityInfo.reviewers.length > 0 ? (
                        <Stack spacing={0.5}>
                          {entityInfo.reviewers.slice(0, 3).map((r) => (
                            <Typography key={r.id} variant="body2">
                              {r.display_name}
                            </Typography>
                          ))}
                          {entityInfo.reviewers.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{entityInfo.reviewers.length - 3} more
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">None assigned</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Reviewees */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <GroupIcon color="warning" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Reviewees
                        </Typography>
                        <Chip label={entityInfo.reviewees.length} size="small" color="warning" />
                      </Stack>
                      {entityInfo.reviewees.length > 0 ? (
                        <Stack spacing={0.5}>
                          {entityInfo.reviewees.slice(0, 3).map((r) => (
                            <Typography key={r.id} variant="body2">
                              {r.display_name}
                            </Typography>
                          ))}
                          {entityInfo.reviewees.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{entityInfo.reviewees.length - 3} more
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">None assigned</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Parent Entities Navigation */}
                {entityInfo.entity.parents && entityInfo.entity.parents.length > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Parent Entities
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {entityInfo.entity.parents.map((parent: any) => (
                            <Chip
                              key={parent.id}
                              label={parent.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() => onEntitySelect?.(parent.id)}
                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
                            />
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}

      </Grid>
    </Box>
  );
};

export default DemographicsTab;
